import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Scroll,
    Shield,
    Swords,
    Coins,
    Crown,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Heart,
    ArrowUpCircle
} from 'lucide-react';
import { DRAFT_COSTS } from '../utils/gameRules';

const SectionFn: React.FC<{
    title: string;
    icon: React.ComponentType<any>;
    children: React.ReactNode;
    defaultOpen?: boolean;
}> = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = React.useState(defaultOpen);

    return (
        <div className="border border-zinc-800 bg-zinc-950/50 rounded-lg overflow-hidden mb-4 transition-all hover:border-zinc-700">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-6 bg-zinc-900/50 hover:bg-zinc-900 transition-colors"
            >
                <div className="flex items-center gap-4">
                    <div className="p-2 rounded bg-tudor-gold/10 text-tudor-gold">
                        <Icon size={20} />
                    </div>
                    <h3 className="text-lg font-serif tracking-widest text-zinc-200 uppercase">{title}</h3>
                </div>
                {isOpen ? <ChevronUp size={20} className="text-zinc-500" /> : <ChevronDown size={20} className="text-zinc-500" />}
            </button>

            {isOpen && (
                <div className="p-6 border-t border-zinc-800/50 text-zinc-400 leading-relaxed text-sm md:text-base animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

const Rulebook: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-tudor-black p-4 md:p-10 pb-20 font-mono">
            <div className="max-w-4xl mx-auto space-y-8">

                {/* Header */}
                <header className="text-center mb-12 animate-fade-in-up">
                    <div className="inline-block p-4 rounded-full bg-tudor-gold/5 border border-tudor-gold/20 mb-6">
                        <Scroll className="w-12 h-12 text-tudor-gold" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-serif text-tudor-gold tracking-[0.2em] uppercase mb-4">
                        The Tudor Codex
                    </h1>
                    <p className="text-zinc-500 max-w-lg mx-auto uppercase tracking-widest text-xs">
                        Official regulations of the Royal Court
                    </p>
                </header>

                {/* Introduction */}
                <div className="p-6 rounded-lg bg-zinc-900/30 border border-zinc-800 text-center mb-8">
                    <p className="italic text-zinc-400 font-serif text-lg">
                        "War is not won by a single battle, but by the endurance of the crown."
                    </p>
                </div>

                {/* SECTIONS */}
                <div className="space-y-4">

                    {/* 1. THE DRAFT */}
                    <SectionFn title="The Draft" icon={Coins} defaultOpen={true}>
                        <p className="mb-6">
                            Before the first trumpet sounds, you must assemble your forces. You are granted an initial treasury of <span className="text-tudor-gold font-bold">Gold</span> based on the selected campaign difficulty. Spend it wisely to construct a balanced army.
                        </p>

                        <h4 className="text-white font-bold uppercase tracking-wider mb-4 text-xs">Unit Conscription Costs</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                            {Object.entries(DRAFT_COSTS).map(([key, cost]) => (
                                <div key={key} className="flex items-center justify-between p-3 rounded bg-black/40 border border-zinc-800">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl">{
                                            key === 'p' ? '♟' : key === 'n' ? '♞' : key === 'b' ? '♝' : key === 'r' ? '♜' : key === 'q' ? '♛' : '♚'
                                        }</span>
                                        <span className="uppercase text-xs text-zinc-500">{
                                            key === 'p' ? 'Pawn' : key === 'n' ? 'Knight' : key === 'b' ? 'Bishop' : key === 'r' ? 'Rook' : key === 'q' ? 'Queen' : 'King'
                                        }</span>
                                    </div>
                                    <span className="text-tudor-gold font-mono">{cost}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-zinc-500">
                            * The King is mandatory and incurs no cost. You may commission only one Queen initially.
                        </p>
                    </SectionFn>

                    {/* 2. WAR OF ATTRITION */}
                    <SectionFn title="War of Attrition" icon={Shield}>
                        <p className="mb-4">
                            Unlike traditional skirmishes, your pieces in Tudor Chess are <span className="text-red-400 font-bold">mortal</span>.
                        </p>
                        <ul className="list-disc pl-5 space-y-2 mb-6 marker:text-tudor-gold">
                            <li>Units lost in battle are removed from your <span className="text-white">Reserve Inventory</span> permanently.</li>
                            <li>Winning a battle does not restore your fallen soldiers.</li>
                            <li>If you run out of pieces to field a legal army (King + sufficient Pawns), the War is lost.</li>
                            <li><span className="text-tudor-gold font-bold">The Price of Succession:</span> When a King falls, a new one must be elected (Cost: 1 Pawn). However, repeated failures cause mass desertions. The 2nd King causes 15% of your pawns to flee, the 3rd causes 30%, and so on.</li>
                        </ul>
                        <div className="p-4 bg-red-900/10 border border-red-900/30 rounded flex items-start gap-3">
                            <AlertCircle className="text-red-500 shrink-0 mt-1" size={16} />
                            <p className="text-red-400 text-xs uppercase tracking-wide">
                                <span className="font-bold">Total Defeat:</span> Occurs if your defensive line falters (less than 8 pawns remaining).
                            </p>
                        </div>
                    </SectionFn>

                    {/* 3. BATTLE MECHANICS */}
                    <SectionFn title="Battle Protocols" icon={Swords} defaultOpen={true}>
                        <div className="space-y-6">
                            <div>
                                <h4 className="text-white font-bold uppercase tracking-wider mb-2 text-sm flex items-center gap-2">
                                    <Shield size={14} className="text-tudor-gold" /> Deployment Phase
                                </h4>
                                <p className="mb-2">
                                    Before each battle, you may choose which Elite pieces (Rooks, Knights, Bishops, Queen) to deploy from your Reserve.
                                </p>
                                <p className="text-xs text-zinc-500">
                                    Tip: Conserve your strongest units for critical battles. A reckless commander will find themselves with nothing but pawns.
                                </p>
                            </div>

                            <div className="h-px bg-zinc-800"></div>

                            <div>
                                <h4 className="text-white font-bold uppercase tracking-wider mb-2 text-sm flex items-center gap-2">
                                    <Heart size={14} className="text-red-400" /> Mercy of the King
                                </h4>
                                <p>
                                    A wise ruler knows when the field is lost. You may <span className="text-white font-bold uppercase">Surrender</span> a battle at any time.
                                </p>
                                <p className="mt-2 text-white/80 bg-zinc-800/50 p-3 rounded border-l-2 border-tudor-gold">
                                    Effect: The opponent's army is spared. Any pieces they lost during that specific battle are <span className="text-green-400">returned to their reserve</span>. Your losses remain permanent.
                                </p>
                            </div>

                            <div className="h-px bg-zinc-800"></div>

                            <div>
                                <h4 className="text-white font-bold uppercase tracking-wider mb-2 text-sm flex items-center gap-2">
                                    <ArrowUpCircle size={14} className="text-green-400" /> Battlefield Commission
                                </h4>
                                <p className="mb-2">
                                    A Pawn that reaches the enemy's back rank is promoted.
                                    This promotion is <span className="text-tudor-gold font-bold">permanent</span>. The new piece is added to your Reserve Inventory for future battles.
                                </p>
                                <div className="p-3 border border-zinc-700 bg-black/20 rounded">
                                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1 font-bold">Royal Decree (Restriction)</p>
                                    <p className="text-sm">
                                        You may not promote a Pawn to a <span className="text-white">Queen</span> if you already command a living Queen. You must choose a Rook, Bishop, or Knight instead.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </SectionFn>

                    {/* 4. RANKED WARFARE */}
                    <SectionFn title="Ranked Warfare" icon={Crown}>
                        <div className="space-y-4">
                            <p className="mb-2">
                                Commanders who prove their worth may enter Ranked Warfare. Victory here elevates your standing across the Realm.
                            </p>

                            <div className="bg-zinc-900 border border-zinc-800 p-4 rounded mb-4">
                                <h4 className="text-tudor-gold font-bold uppercase text-xs mb-2 tracking-wider">Eligibility Requirements</h4>
                                <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
                                    <li className="text-tudor-gold">Ranked Warfare is now <span className="text-white font-bold">Free to Play</span>.</li>
                                    <li>Must maintain a positive Treasury (Gold &gt; 0).</li>
                                </ul>
                            </div>

                            <div className="bg-red-900/10 border border-red-900/30 p-4 rounded">
                                <h4 className="text-red-400 font-bold uppercase text-xs mb-2 tracking-wider">The Law of Abdication</h4>
                                <p className="text-sm text-zinc-400 mb-2">
                                    Should a Commander fall into bankruptcy (0 Gold) or disgrace, they are stripped of their Rank.
                                </p>
                                <p className="text-sm text-zinc-400">
                                    To regain favor, one must formally <span className="text-red-400 font-bold">Abdicate</span> (resetting Gold & Rank) and complete a new Unranked Campaign to prove their competence.
                                </p>
                            </div>
                        </div>
                    </SectionFn>

                    {/* 5. WARTIME REGULATIONS */}
                    <SectionFn title="Wartime Regulations" icon={AlertCircle}>
                        <ul className="space-y-4 text-sm">
                            <li className="flex gap-3">
                                <span className="text-tudor-gold font-bold">I.</span>
                                <span><strong className="text-zinc-300">Deployment Deadline:</strong> Once a War is declared, opponents have <span className="text-white font-bold">24 Hours</span> to deploy their forces. Failure to deploy results in an automatic <span className="text-red-400">Forfeit</span>.</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="text-tudor-gold font-bold">II.</span>
                                <span><strong className="text-zinc-300">Conscription Limits:</strong> All Commanders are limited to <span className="text-white font-bold">3 Active Ranked Wars</span> at a time. Unranked Skirmishes are reserved for those who offer Tribute (Premium).</span>
                            </li>
                        </ul>
                    </SectionFn>

                    {/* 6. ECONOMY */}
                    <SectionFn title="Royal Treasury" icon={Coins}>
                        <p className="mb-4">
                            <span className="text-tudor-gold font-bold">Gold</span> is the lifeblood of your army.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-zinc-900 border border-zinc-800 rounded">
                                <h4 className="text-white font-bold uppercase mb-2 text-xs">Earning Gold</h4>
                                <p className="text-sm text-zinc-400">
                                    Capturing enemy pieces in Ranked matches rewards Gold. Completing Campaigns grants substantial Treasury bonuses.
                                </p>
                            </div>
                            <div className="p-4 bg-zinc-900 border border-zinc-800 rounded">
                                <h4 className="text-white font-bold uppercase mb-2 text-xs">Spending Gold</h4>
                                <p className="text-sm text-zinc-400">
                                    Gold is required to draft units for Ranked Wars. High-tier brackets require significant upfront investment but yield greater rewards.
                                </p>
                            </div>
                        </div>
                    </SectionFn>


                </div>

                {/* Footer Action */}
                <div className="text-center mt-12">
                    <button
                        onClick={() => navigate('/court')}
                        className="text-zinc-500 hover:text-white uppercase tracking-widest text-xs transition-colors"
                    >
                        Return to The Court
                    </button>
                </div>

            </div>
        </div>
    );
};

export default Rulebook;
